/**
 * V2PaywallMediaSection — V2-native editor section for paywall media + gradient
 *
 * Edits: Media (Image/Video), media top/bottom alignment, Gradient overlay
 * V2 paths:
 * - content.featuredMedia (media content)
 * - content.paywall.bodyAppearance.overlay (gradient)
 * - content.featuredMedia.appearance.topAlignment (media/header relationship)
 * - content.featuredMedia.appearance.bottomAlignment (media/content relationship)
 *
 * Replaces: OnboardingPaywallMediaSection (for V2 context)
 * Reuses: ImageUploadElement, VideoUploadElement, StyleFieldRow, MultiLocaleUploadDialog
 *
 * Key V2 differences:
 * - Image: content.featuredMedia.content.image.translations.{locale} (was .l10n.{locale}.assetUrl)
 * - Video: content.featuredMedia.content.video.translations.{locale} (was .video.l10n.{locale}.assetUrl)
 * - Gradient: content.paywall.bodyAppearance.overlay (was styles.bodyStyle)
 * - Legacy position fallback: content.paywall.bodyAppearance.heroAnchor (was styles.imageVerticalPosition)
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useState, useMemo, useRef } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Divider,
  Slider,
  Collapse,
  IconButton,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion } from '@modules/ui-components';
import { ImageUploadElement } from '../../../factories/elements/ImageUploadElement';
import { VideoUploadElement } from '../../../factories/elements/VideoUploadElement';
import { PaddingElement, type PaddingValues } from '../../../factories/elements/PaddingElement';
import { StyleFieldRow } from '../../shared/styleFields';
import { EditorGuideProvider } from '../../../contexts';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import { toV2AssetReference, toV2VideoAssetReference, readAssetUrl, type V2AssetReference } from '@modules/reactFlow/utils/v2AssetUtils';
import { useMultiLocaleUpload } from '../../../hooks/useMultiLocaleUpload';
import { MultiLocaleUploadDialog } from '../../shared/MultiLocaleUploadDialog';
import {
  V2_MEDIA_KIND_OPTIONS,
  V2_DEFAULT_MEDIA_STRUCTURE,
  V2_DEFAULT_IMAGE_CONTENT,
  V2_DEFAULT_VIDEO_CONTENT,
  type V2MediaKind,
} from '../../../config/v2MediaConfig';
import {
  V2_DEFAULT_GRADIENT_STRUCTURE,
  V2_PAYWALL_GRADIENT_COLOR_CONFIG,
} from '../../../config/v2PaywallMediaConfig';
import {
  V2_MEDIA_APPEARANCE_FIELDS,
  V2_MEDIA_BOTTOM_ALIGNMENT_FIELD,
  V2_MEDIA_REPEAT_FIELD,
  V2_MEDIA_TOP_ALIGNMENT_FIELD,
} from '../../../config/v2FlexibleMediaConfig';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM, FIELD_GAP, FIELD_STYLE_GRID_GAP } from '../../../config/ui';
import {
  checkboxSx, checkboxIconSx, checkboxCheckedIconSx,
  expandedToggleButtonSx, expandedToggleButtonActiveSx, expandedSectionSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import { getSectionTestId, getStyleCheckboxTestId, getStyleInputTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';

export interface V2PaywallMediaSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  availableLocales?: string[];
}

export const V2PaywallMediaSection: React.FC<V2PaywallMediaSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = false,
  projectId,
  onboardingId,
  versionId,
  availableLocales = [],
}) => {
  const [uploading, setUploading] = useState(false);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const [appearanceExpanded, setAppearanceExpanded] = useState(false);
  const [showLocaleDialog, setShowLocaleDialog] = useState(false);
  const [pendingUploadAssetUrl, setPendingUploadAssetUrl] = useState<V2AssetReference | null>(null);
  const [pendingMediaType, setPendingMediaType] = useState<'image' | 'video'>('image');
  const lastUploadedAssetUrlRef = useRef<V2AssetReference | null>(null);

  const { savedPreference, shouldShowDialog, savePreference } = useMultiLocaleUpload();
  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);

  // ── Media state ──
  const isMediaEnabled = useMemo(
    () => isFieldEnabled(step, 'content.featuredMedia'),
    [step]
  );

  const mediaKind = useMemo<V2MediaKind>(() => {
    const kind = getValueByPath<string>(step, 'content.featuredMedia.kind');
    return (kind as V2MediaKind) ?? 'Image';
  }, [step]);

  // V2 image: content.featuredMedia.content.image.translations.{locale}
  const imageUrl = useMemo(() => {
    const asset = getValueByPath<Record<string, string>>(
      step,
      `content.featuredMedia.content.image.translations.${locale}`
    );
    return readAssetUrl(asset);
  }, [step, locale]);

  // V2 video: content.featuredMedia.content.video.translations.{locale}
  const videoUrl = useMemo(() => {
    const asset = getValueByPath<Record<string, string>>(
      step,
      `content.featuredMedia.content.video.translations.${locale}`
    );
    return readAssetUrl(asset);
  }, [step, locale]);

  // ── Gradient state ──
  const isGradientEnabled = useMemo(() => {
    const overlay = getValueByPath(step, 'content.paywall.bodyAppearance.overlay');
    const overlayKind = getValueByPath<string | null>(step, 'content.paywall.bodyAppearance.overlayKind');
    return overlay !== null && overlay !== undefined && (!overlayKind || overlayKind === 'gradient');
  }, [step]);

  const gradientHeight = useMemo(() => {
    return getValueByPath<number>(step, 'content.paywall.bodyAppearance.overlay.heightPercentage') ?? 50;
  }, [step]);

  const gradientColor = useMemo(() => {
    const rawColors = getValueByPath<string[] | Record<string, string>>(
      step,
      'content.paywall.bodyAppearance.overlay.colors'
    );
    const colors = Array.isArray(rawColors) ? rawColors : rawColors ? Object.values(rawColors) : undefined;
    return colors?.[0] ?? '#000000';
  }, [step]);

  const legacyHeroAnchor = useMemo(() => {
    return getValueByPath<string>(step, 'content.paywall.bodyAppearance.heroAnchor');
  }, [step]);

  const mediaTopAlignment = useMemo(() => {
    return getValueByPath<string>(step, V2_MEDIA_TOP_ALIGNMENT_FIELD.path) ?? 'top';
  }, [step]);

  const mediaBottomAlignment = useMemo(() => {
    return getValueByPath<string>(step, V2_MEDIA_BOTTOM_ALIGNMENT_FIELD.path)
      ?? (legacyHeroAnchor === 'HeaderBottom' ? 'contentBottom' : 'contentTop');
  }, [step, legacyHeroAnchor]);

  const mediaPaddingValues = useMemo<PaddingValues>(() => {
    const insets = getValueByPath<Record<string, unknown>>(step, 'content.featuredMedia.layout.insets');
    return {
      paddingTop: typeof insets?.top === 'number' ? insets.top : 0,
      paddingRight: typeof insets?.end === 'number' ? insets.end : 0,
      paddingBottom: typeof insets?.bottom === 'number' ? insets.bottom : 0,
      paddingLeft: typeof insets?.start === 'number' ? insets.start : 0,
    };
  }, [step]);

  // ── Media handlers ──
  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const handleMediaEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      onChange('content.featuredMedia', enabled ? V2_DEFAULT_MEDIA_STRUCTURE : null);
    },
    [onChange]
  );

  const handleKindChange = useCallback(
    (event: SelectChangeEvent) => {
      const newKind = event.target.value as V2MediaKind;
      const currentMedia = getValueByPath<Record<string, unknown>>(step, 'content.featuredMedia');
      const updatedMedia = {
        ...currentMedia,
        kind: newKind,
        content: newKind === 'Image' ? V2_DEFAULT_IMAGE_CONTENT : V2_DEFAULT_VIDEO_CONTENT,
      };
      onChange('content.featuredMedia', updatedMedia);
    },
    [onChange, step]
  );

  const handleImageChange = useCallback(
    (value: string | null) => {
      if (value) {
        const assetRef = lastUploadedAssetUrlRef.current ?? { original: value, small: value };
        onChange(`content.featuredMedia.content.image.translations.${locale}`, assetRef);
        lastUploadedAssetUrlRef.current = null;
      } else {
        onChange(`content.featuredMedia.content.image.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleVideoChange = useCallback(
    (value: string | null) => {
      if (value) {
        const assetRef = lastUploadedAssetUrlRef.current ?? { original: value, small: value };
        onChange(`content.featuredMedia.content.video.translations.${locale}`, assetRef);
        lastUploadedAssetUrlRef.current = null;
      } else {
        onChange(`content.featuredMedia.content.video.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      setUploading(true);
      try {
        const result = await uploadService.uploadImage(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        });
        const v2Asset = toV2AssetReference(result.assetUrl);
        lastUploadedAssetUrlRef.current = v2Asset;

        if (shouldShowDialog(availableLocales)) {
          setPendingUploadAssetUrl(v2Asset);
          setPendingMediaType('image');
          setShowLocaleDialog(true);
        } else if (savedPreference === 'all' && availableLocales.length > 1) {
          for (const loc of availableLocales) {
            onChange(`content.featuredMedia.content.image.translations.${loc}`, v2Asset);
          }
        }
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, availableLocales, shouldShowDialog, savedPreference, onChange]
  );

  const handleVideoUpload = useCallback(
    async (file: File, onProgress?: (fraction: number) => void): Promise<string> => {
      setUploading(true);
      try {
        const result = await uploadService.uploadVideo(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        }, { onProgress });
        const v2Asset = toV2AssetReference(result.assetUrl);
        lastUploadedAssetUrlRef.current = v2Asset;

        if (shouldShowDialog(availableLocales)) {
          setPendingUploadAssetUrl(v2Asset);
          setPendingMediaType('video');
          setShowLocaleDialog(true);
        } else if (savedPreference === 'all' && availableLocales.length > 1) {
          for (const loc of availableLocales) {
            onChange(`content.featuredMedia.content.video.translations.${loc}`, toV2VideoAssetReference(result.assetUrl));
          }
        }
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, availableLocales, shouldShowDialog, savedPreference, onChange]
  );

  const handleStyleChange = useCallback(
    (stylePath: string, value: unknown) => onChange(stylePath, value),
    [onChange]
  );

  const handleMediaPaddingChange = useCallback(
    (path: string, value: number) => {
      const insetsPath = path
        .replace(/\.paddingTop$/, '.top')
        .replace(/\.paddingRight$/, '.end')
        .replace(/\.paddingBottom$/, '.bottom')
        .replace(/\.paddingLeft$/, '.start');
      onChange(insetsPath, value);
    },
    [onChange]
  );

  // ── Gradient handlers ──
  const handleGradientToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const enabled = event.target.checked;
      onChange('content.paywall.bodyAppearance.overlay', enabled ? V2_DEFAULT_GRADIENT_STRUCTURE : null);
      onChange('content.paywall.bodyAppearance.overlayKind', enabled ? 'gradient' : null);
    },
    [onChange]
  );

  const handleGradientHeightChange = useCallback(
    (_event: Event, value: number | number[]) => {
      const newHeight = typeof value === 'number' ? value : value[0];
      onChange('content.paywall.bodyAppearance.overlay.heightPercentage', newHeight);
    },
    [onChange]
  );

  const handleGradientColorChange = useCallback(
    (value: unknown) => {
      const color = value as string;
      const rawColors = getValueByPath<string[] | Record<string, string>>(
        step,
        'content.paywall.bodyAppearance.overlay.colors'
      );
      const currentColors = Array.isArray(rawColors)
        ? rawColors
        : rawColors ? Object.values(rawColors) : ['#000000', '#00000000'];
      onChange('content.paywall.bodyAppearance.overlay.colors', [color, currentColors[1]]);
    },
    [step, onChange]
  );

  const handleMediaTopAlignmentChange = useCallback(
    (value: unknown) => onChange(V2_MEDIA_TOP_ALIGNMENT_FIELD.path, value),
    [onChange]
  );

  const handleMediaBottomAlignmentChange = useCallback(
    (value: unknown) => onChange(V2_MEDIA_BOTTOM_ALIGNMENT_FIELD.path, value),
    [onChange]
  );

  // ── Multi-locale dialog ──
  const applyMediaToLocale = useCallback(
    (assetRef: V2AssetReference, targetLocale: string, mediaType: 'image' | 'video') => {
      const contentKey = mediaType === 'image' ? 'image' : 'video';
      onChange(`content.featuredMedia.content.${contentKey}.translations.${targetLocale}`, assetRef);
    },
    [onChange]
  );

  const handleApplyToAllLocales = useCallback(
    (rememberChoice: boolean) => {
      if (!pendingUploadAssetUrl) return;
      if (rememberChoice) savePreference('all');
      for (const loc of availableLocales) {
        applyMediaToLocale(pendingUploadAssetUrl, loc, pendingMediaType);
      }
      setShowLocaleDialog(false);
      setPendingUploadAssetUrl(null);
    },
    [pendingUploadAssetUrl, availableLocales, pendingMediaType, applyMediaToLocale, savePreference]
  );

  const handleApplyToCurrentLocale = useCallback(
    (rememberChoice: boolean) => {
      if (!pendingUploadAssetUrl) return;
      if (rememberChoice) savePreference('current');
      applyMediaToLocale(pendingUploadAssetUrl, locale, pendingMediaType);
      setShowLocaleDialog(false);
      setPendingUploadAssetUrl(null);
    },
    [pendingUploadAssetUrl, locale, pendingMediaType, applyMediaToLocale, savePreference]
  );

  const handleCancelLocaleDialog = useCallback(() => {
    setShowLocaleDialog(false);
    setPendingUploadAssetUrl(null);
  }, []);

  // Placeholder config for upload elements (V2 media doesn't have style fields)
  const mediaImageConfig = useMemo(() => ({
    path: 'content.featuredMedia.content.image',
    label: 'Image',
    type: 'imageUpload' as const,
    defaultValue: '',
    disableBehavior: 'null' as const,
    guideKey: 'screenedit-paywall-media-image',
  }), []);

  const mediaVideoConfig = useMemo(() => ({
    path: 'content.featuredMedia.content.video',
    label: 'Video',
    type: 'videoUpload' as const,
    defaultValue: '',
    disableBehavior: 'null' as const,
    guideKey: 'screenedit-paywall-media-video',
  }), []);

  return (
    <SAccordion
      title="Media"
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId('paywall-media')}
    >
      <EditorGuideProvider>
        {/* Enable Media */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP, mb: FIELD_MARGIN_BOTTOM }}
        >
          <Checkbox
            data-testid="paywall-media-enable"
            checked={isMediaEnabled}
            onChange={handleMediaEnabledChange}
            disabled={disabled}
            size="small"
            sx={checkboxSx}
            icon={<Box sx={checkboxIconSx} />}
            checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          />
          <Typography variant="body2" color="text.secondary">
            Enable
          </Typography>
        </Box>

        {/* Kind Selector + Appearance toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: FIELD_MARGIN_BOTTOM }}>
          <FormControl size="small" sx={{ flex: 1 }} data-testid="paywall-media-kind">
            <Select
              value={mediaKind}
              onChange={handleKindChange}
              disabled={disabled || !isMediaEnabled}
            >
              {V2_MEDIA_KIND_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            aria-label="Toggle media settings"
            data-testid="paywall-media-settings-toggle"
            size="small"
            onClick={() => setAppearanceExpanded(prev => !prev)}
            disabled={disabled || !isMediaEnabled}
            sx={appearanceExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Settings panel: media appearance + Paywall-specific media settings */}
        <Collapse in={appearanceExpanded && isMediaEnabled} timeout="auto" unmountOnExit>
          <Box sx={expandedSectionSx}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: FIELD_STYLE_GRID_GAP }}>
              {V2_MEDIA_APPEARANCE_FIELDS.map((fieldConfig) => (
                <StyleFieldRow
                  key={fieldConfig.path}
                  config={fieldConfig}
                  value={getValueByPath(step as unknown as Record<string, unknown>, fieldConfig.path)}
                  onChange={(value) => onChange(fieldConfig.path, value)}
                  disabled={disabled}
                  inputTestId={getStyleInputTestId('media', fieldConfig.label)}
                  checkboxTestId={getStyleCheckboxTestId('media', fieldConfig.label)}
                />
              ))}
              {mediaKind === 'Video' && (
                <StyleFieldRow
                  config={V2_MEDIA_REPEAT_FIELD}
                  value={getValueByPath(step as unknown as Record<string, unknown>, V2_MEDIA_REPEAT_FIELD.path)}
                  onChange={(value) => onChange(V2_MEDIA_REPEAT_FIELD.path, value)}
                  disabled={disabled}
                  inputTestId={getStyleInputTestId('media', V2_MEDIA_REPEAT_FIELD.label)}
                  checkboxTestId={getStyleCheckboxTestId('media', V2_MEDIA_REPEAT_FIELD.label)}
                />
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <PaddingElement
                basePath="content.featuredMedia.layout.insets"
                values={mediaPaddingValues}
                onChange={handleMediaPaddingChange}
                disabled={disabled}
                label="Padding"
                data-testid="paywall-media-padding"
              />
            </Box>

            <Box sx={{ mt: 2, mb: FIELD_MARGIN_BOTTOM, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: FIELD_STYLE_GRID_GAP }}>
              <StyleFieldRow
                config={V2_MEDIA_TOP_ALIGNMENT_FIELD}
                value={mediaTopAlignment}
                onChange={handleMediaTopAlignmentChange}
                disabled={disabled}
                inputTestId={getStyleInputTestId('media', V2_MEDIA_TOP_ALIGNMENT_FIELD.label)}
                checkboxTestId={getStyleCheckboxTestId('media', V2_MEDIA_TOP_ALIGNMENT_FIELD.label)}
              />
              <StyleFieldRow
                config={V2_MEDIA_BOTTOM_ALIGNMENT_FIELD}
                value={mediaBottomAlignment}
                onChange={handleMediaBottomAlignmentChange}
                disabled={disabled}
                inputTestId={getStyleInputTestId('media', V2_MEDIA_BOTTOM_ALIGNMENT_FIELD.label)}
                checkboxTestId={getStyleCheckboxTestId('media', V2_MEDIA_BOTTOM_ALIGNMENT_FIELD.label)}
              />
            </Box>

            {/* Gradient Section */}
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Gradient
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP, mb: FIELD_MARGIN_BOTTOM }}>
              <Checkbox
                checked={isGradientEnabled}
                onChange={handleGradientToggle}
                disabled={disabled}
                size="small"
                inputProps={{ 'aria-label': 'Enable Gradient', 'data-testid': 'paywall-media-gradient-enable' } as React.InputHTMLAttributes<HTMLInputElement>}
                sx={checkboxSx}
                icon={<Box sx={checkboxIconSx} />}
                checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
              />
              <Typography variant="body2" color="text.secondary">
                Enable Gradient
              </Typography>
            </Box>

            {isGradientEnabled && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Height</Typography>
                    <Typography variant="caption" color="text.primary" fontWeight={500}>
                      {gradientHeight}%
                    </Typography>
                  </Box>
                  <Slider
                    value={gradientHeight}
                    onChange={handleGradientHeightChange}
                    min={0}
                    max={100}
                    step={5}
                    disabled={disabled}
                    size="small"
                    data-testid="paywall-media-gradient-height-slider"
                  />
                </Box>
                <StyleFieldRow
                  config={V2_PAYWALL_GRADIENT_COLOR_CONFIG}
                  value={gradientColor}
                  onChange={handleGradientColorChange}
                  disabled={disabled}
                  inputTestId="input-style-paywall-gradient-color"
                />
              </Box>
            )}
          </Box>
        </Collapse>

        {/* Image upload */}
        {mediaKind === 'Image' && (
          <Box>
            <ImageUploadElement
              data-testid="paywall-media-image"
              fieldConfig={mediaImageConfig}
              value={imageUrl}
              templateValue={null}
              enabled={true}
              locale={locale}
              onChange={handleImageChange}
              onEnabledChange={() => {}}
              onUpload={handleImageUpload}
              disabled={disabled || uploading || !isMediaEnabled}
              hideCheckbox={true}
              expandedStyles={expandedFields.has('media-image')}
              onStylesExpandToggle={() => toggleFieldExpansion('media-image')}
              styleValues={{}}
              onStyleChange={handleStyleChange}
            />
          </Box>
        )}

        {/* Video upload */}
        {mediaKind === 'Video' && (
          <Box>
            <VideoUploadElement
              data-testid="paywall-media-video"
              fieldConfig={mediaVideoConfig}
              value={videoUrl}
              templateValue={null}
              enabled={true}
              locale={locale}
              onChange={handleVideoChange}
              onEnabledChange={() => {}}
              onUpload={handleVideoUpload}
              disabled={disabled || uploading || !isMediaEnabled}
              hideCheckbox={true}
              expandedStyles={expandedFields.has('media-video')}
              onStylesExpandToggle={() => toggleFieldExpansion('media-video')}
              styleValues={{}}
              onStyleChange={handleStyleChange}
            />
          </Box>
        )}

      </EditorGuideProvider>

      <MultiLocaleUploadDialog
        open={showLocaleDialog}
        onClose={handleCancelLocaleDialog}
        mediaType={pendingMediaType}
        currentLocale={locale}
        availableLocales={availableLocales}
        onApplyAll={handleApplyToAllLocales}
        onApplyCurrent={handleApplyToCurrentLocale}
      />
    </SAccordion>
  );
};

export default V2PaywallMediaSection;
